import React from 'react';
import SmartLink from '@/components/SmartLink';

interface FooterLink {
  label: string;
  to?: string;
  href?: string;
  target?: string;
  suffix?: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
  extraTitle?: string;
  extraLinks?: FooterLink[];
}

const FOOTER_COLUMNS: FooterColumn[] = [
  {
    title: 'ソリューション',
    links: [{ label: '商業施設・百貨店', to: '/solution#case-study' }],
    extraTitle: '製品',
    extraLinks: [{ label: 'GBase Concierge', to: '/concierge' }],
  },
  {
    title: '機能',
    links: [
      { label: 'FAQ・施設案内AI', to: '/solution#feature-faq' },
      { label: 'フロアマップ・ナビゲーション', to: '/solution#feature-floormap' },
      { label: 'リアルタイム会話', to: '/solution#feature-realtime' },
    ],
  },
  {
    title: 'リソース',
    links: [
      { label: '導入事例', to: '/showcase' },
      { label: 'ブログ', href: '/blog' },
      { label: 'マニュアル', href: 'https://docs.gbase.ai/user-manual/quickstart', target: '_blank' },
      { label: 'リリースノート', href: 'https://cs.gbase.ai/blog/release-notes/' },
      { label: '料金', to: '/pricing' },
    ],
  },
  {
    title: 'その他のプロダクト',
    links: [
      { label: 'GBase', suffix: '（ナレッジ AI）', href: 'https://gbase.ai/', target: '_blank' },
      { label: 'GBase OnPrem', suffix: '（オンプレ AI）', href: 'https://onprem.gbase.ai/', target: '_blank' },
      { label: 'Felo Subtitles', suffix: '（多言語字幕）', href: 'https://subtitles.felo.me/', target: '_blank' },
      { label: 'GBase GTM', suffix: '（顧客獲得 AI）', href: 'https://s.gbase.ai/', target: '_blank' },
    ],
  },
];

const SPARTICLE_LINKS: FooterLink[] = [
  { label: '会社概要', href: 'https://www.sparticle.com/', target: '_blank' },
  { label: '採用情報', href: 'https://www.sparticle.com/careers', target: '_blank' },
];

const LEGAL_LINKS: FooterLink[] = [
  { label: 'プライバシーポリシー', to: '/privacy-policy' },
  { label: '利用規約', to: '/terms-of-use' },
  { label: '特定商取引法', to: '/legal/specific-based' },
];

const SOCIAL_LINKS = [
  {
    label: 'PR TIMES',
    url: 'https://prtimes.jp/main/html/searchrlp/company_id/87269',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <rect width="24" height="24" rx="4" fill="currentColor" />
        <text
          x="12"
          y="17.5"
          fontFamily="Inter, 'Noto Sans JP', sans-serif"
          fontSize="14"
          fontWeight="900"
          textAnchor="middle"
          fill="#FFFFFF"
        >
          P
        </text>
      </svg>
    ),
  },
  {
    label: 'X (Twitter)',
    url: 'https://x.com/sparticle_ai',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    url: 'https://jp.linkedin.com/company/sparticle-inc',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zm1.78 13.02H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.55C0 23.23.79 24 1.77 24h20.45C23.2 24 24 23.23 24 22.28V1.72C24 .77 23.2 0 22.22 0z" />
      </svg>
    ),
  },
  {
    label: 'note',
    url: 'https://note.com/sparticle_gbase',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M0 .279c4.623 0 10.953-.235 15.498-.117 6.099.156 8.39 2.813 8.468 9.374.077 3.71 0 14.335 0 14.335h-6.598c0-9.296.04-10.83 0-13.759-.078-2.578-.814-3.807-2.795-4.041-2.097-.235-7.975-.04-7.975-.04v17.84H0Z" />
      </svg>
    ),
  },
  {
    label: 'YouTube',
    url: 'https://www.youtube.com/@sparticle_gbase',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
];

const LinkItem: React.FC<{ link: FooterLink }> = ({ link }) => (
  <li>
    <SmartLink
      to={link.to}
      href={link.href}
      target={link.target}
      className="text-gray-600 hover:text-orange-500 transition-colors whitespace-nowrap"
    >
      {link.label}
      {link.suffix && <span className="text-gray-400 ml-1 text-xs">{link.suffix}</span>}
    </SmartLink>
  </li>
);

const Footer = () => {
  return (
    <footer className="bg-white border-t border-gray-200 pt-16 pb-8">
      <div className="px-4 md:px-10 lg:px-40 flex justify-center">
        <div className="w-full max-w-[1200px]">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {FOOTER_COLUMNS.map((col, idx) => (
              <div key={idx}>
                <p className="font-semibold text-gray-900 text-sm mb-4">{col.title}</p>
                <ul className="space-y-2 text-sm">
                  {col.links.map((link, i) => (
                    <LinkItem key={i} link={link} />
                  ))}
                </ul>
                {col.extraTitle && col.extraLinks && (
                  <>
                    <p className="font-semibold text-gray-900 text-sm mt-6 mb-4">{col.extraTitle}</p>
                    <ul className="space-y-2 text-sm">
                      {col.extraLinks.map((link, i) => (
                        <LinkItem key={i} link={link} />
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ))}

            {/* Sparticle について */}
            <div>
              <p className="font-semibold text-gray-900 text-sm mb-4">Sparticle について</p>
              <ul className="space-y-2 text-sm">
                {SPARTICLE_LINKS.map((link, i) => (
                  <LinkItem key={i} link={link} />
                ))}
              </ul>
              <div className="flex gap-3 mt-4">
                {SOCIAL_LINKS.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    title={social.label}
                    className="text-gray-500 hover:text-violet-500 transition-colors"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8 flex flex-wrap justify-between items-center gap-4">
            <p className="text-xs text-gray-500">© 2026 Sparticle Inc. All rights reserved.</p>
            <div className="flex gap-6 text-xs">
              {LEGAL_LINKS.map((link, i) => (
                <SmartLink
                  key={i}
                  to={link.to}
                  href={link.href}
                  className="text-gray-500 hover:text-orange-500 transition-colors"
                >
                  {link.label}
                </SmartLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
